import clsx from "classnames"
import { ContentSummaryTag } from "common/components/ContentSummaryTag"
import { Heading } from "common/components/Heading"
import { ImgixImage, ImgixImageProps } from "common/components/ImgixImage"
import { closestCrop } from "modules/editor/utils/closestCrop"
import { ImageAspectRatio } from "modules/editor/types"
import type { ContentSummaryVariantProps } from "common/components/ContentSummary/types"
import { Image, Maybe } from "common/types/graphql"

interface HeroSummaryImageProps {
  image?: Maybe<Partial<Image>>
  imageSizes?: string
  desktopImageAspectRatio: ImageAspectRatio
}

const GradientOverlay = () => (
  <div
    data-testid="gradient-overlay"
    className="absolute inset-0 bg-linear-to-t from-black from-20% to-60% opacity-85"
  ></div>
)

/**
 * `HeroSummaryImage` is a functional component that renders an image with a gradient overlay, given the image prop.
 * In case the image prop is undefined, the component will return null.
 * The component uses the `closestCrop` function to determine the best crop for the image based on the provided aspect ratio.
 * Widescreen desktopImageAspectRatio is no longer supported as it was causing double cropping. Since it is not a crop we collect.
 */
const HeroSummaryImage = ({
  image,
  desktopImageAspectRatio,
  imageSizes,
}: HeroSummaryImageProps) => {
  // This is no longer supporting passing in widescreen ratio to prevent more being added
  if (image?.src === undefined || desktopImageAspectRatio === ImageAspectRatio.Widescreen) {
    return null
  }

  const imageProps: ImgixImageProps = {
    ...image,
    src: image.src,
    sizes: imageSizes,
  }
  const squareCrop = closestCrop(ImageAspectRatio.Square, image.crops || [])
  const crop = closestCrop(desktopImageAspectRatio, image.crops || [])

  return (
    <>
      <div
        className={clsx("absolute inset-0", {
          // Note: the square crop is purposefully not here to retain current fake square-ish design behaviour
          "sm:aspect-3/2": desktopImageAspectRatio === ImageAspectRatio.Landscape,
          "sm:aspect-3/1": desktopImageAspectRatio === ImageAspectRatio.Ultrawide,
          "sm:aspect-2/1": desktopImageAspectRatio === ImageAspectRatio.Pano,
        })}
      >
        <div className="absolute inset-0 hidden sm:block">
          <ImgixImage {...imageProps} crop={crop} alt={image.alt} />
        </div>
        <div className="absolute inset-0 sm:hidden">
          <ImgixImage {...imageProps} crop={squareCrop} alt={image.alt} />
        </div>
        <GradientOverlay />
      </div>
    </>
  )
}

/**
 * The `Hero` component is a variant of the `ContentSummary` component.
 * The summary image will use the crop based on the provided `desktopImageAspectRatio` prop
 * or square for small screens
 */
export const Hero = ({
  title,
  url,
  byline,
  hideByline,
  contentType,
  imageSizes,
  image,
  desktopImageAspectRatio,
  tagLabel,
  tagStyle,
  anchorTarget,
  className,
}: ContentSummaryVariantProps) => (
  <article className="contents">
    <a
      href={url}
      target={anchorTarget}
      className={clsx("group relative flex min-h-96 w-full flex-col justify-end", className, {
        // Note: the square crop is purposefully not here as aspect-square to retain current fake square-ish design behaviour
        "sm:min-h-80 lg:min-h-96 xl:min-h-120":
          !desktopImageAspectRatio || desktopImageAspectRatio === ImageAspectRatio.Square,
        "sm:aspect-3/2 sm:min-h-0": desktopImageAspectRatio === ImageAspectRatio.Landscape,
        "sm:aspect-3/1 sm:min-h-0": desktopImageAspectRatio === ImageAspectRatio.Ultrawide,
        "sm:aspect-2/1 sm:min-h-0": desktopImageAspectRatio === ImageAspectRatio.Pano,
      })}
    >
      {desktopImageAspectRatio && (
        <HeroSummaryImage
          image={image}
          imageSizes={imageSizes}
          desktopImageAspectRatio={desktopImageAspectRatio}
        />
      )}
      <section className="relative flex max-w-(--breakpoint-md) flex-col items-start gap-4 p-4 text-white md:p-8 xl:max-w-(--breakpoint-lg)">
        {<ContentSummaryTag contentType={contentType} tagLabel={tagLabel} tagStyle={tagStyle} />}
        <span className="drop-shadow-dark group-hover:underline">
          <Heading level={3}>
            <span dangerouslySetInnerHTML={{ __html: title || "" }} />
          </Heading>
        </span>
        {byline && !hideByline && (
          <p className="line-clamp-3 text-sm font-bold drop-shadow">{byline}</p>
        )}
      </section>
    </a>
  </article>
)
